import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import {
  FlattenedEpisodeRow,
  FlattenedSaleRow,
  SaleAnswerRow,
  SaleColumnDef,
  SaleTableColumnId,
  SelectedSaleAnswer,
} from '../types/SalesTypes';
import {
  SALES_OVERSCAN,
  SALES_SCROLL_BOTTOM_PAD,
  SALES_ANSWER_ROW_HEIGHT,
  SALES_FORM_ANSWER_ROW_HEIGHT,
  clampDiscount,
  computeRowLayout,
  computeSalePrice,
  episodeCellLabel,
  episodeSubheaderLabel,
  findRowIndexAtOffset,
  isSelectableSaleAnswer,
  saleAnswerKey,
  selectionStateForKeys,
} from '../salesUtils';
import { TriStateCheckbox } from './TriStateCheckbox';
import styles from './SalesTable.module.scss';

interface WindowedSaleTableProps {
  flattenedRows: FlattenedSaleRow[];
  columns: SaleColumnDef[];
  columnWidths: Record<string, number>;
  onColumnWidthChange: (columnId: string, width: number) => void;
  selectedKeys?: Set<string>;
  episodeSelectableKeys?: Map<string, string[]>;
  onToggleRow?: (key: string, checked: boolean) => void;
  onToggleEpisode?: (episodeUuid: string, checked: boolean) => void;
  saleColumnHeaderSlot?: React.ReactNode;
  discountColumnHeaderSlot?: React.ReactNode;
  onDiscountChange?: (key: string, discountPercent: number) => void;
  discountDisabled?: boolean;
}

function tableWidth(columnWidths: Record<string, number>, columns: SaleColumnDef[]): number {
  return columns.reduce((sum, col) => sum + (columnWidths[col.id] ?? col.defaultWidth), 0);
}

function colWidth(columnWidths: Record<string, number>, column: SaleColumnDef): number {
  return columnWidths[column.id] ?? column.defaultWidth;
}

function ColGroup(props: {
  columns: SaleColumnDef[];
  columnWidths: Record<string, number>;
}) {
  const { columns, columnWidths } = props;
  return (
    <colgroup>
      {columns.map((column) => (
        <col key={column.id} style={{ width: colWidth(columnWidths, column) }} />
      ))}
    </colgroup>
  );
}

const EMPTY_KEY_SET: Set<string> = new Set();
const EMPTY_EPISODE_KEYS: Map<string, string[]> = new Map();

export function WindowedSaleTable(props: WindowedSaleTableProps) {
  const {
    flattenedRows,
    columns,
    columnWidths,
    onColumnWidthChange,
    selectedKeys = EMPTY_KEY_SET,
    episodeSelectableKeys = EMPTY_EPISODE_KEYS,
    onToggleRow,
    onToggleEpisode,
    saleColumnHeaderSlot,
    discountColumnHeaderSlot,
    onDiscountChange,
    discountDisabled,
  } = props;

  const bodyScrollerRef = useRef<HTMLDivElement>(null);
  const headerScrollerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(400);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const resizeRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
    minWidth: number;
  } | null>(null);

  const totalWidth = useMemo(
    () => tableWidth(columnWidths, columns),
    [columnWidths, columns],
  );

  const answerRowHeight = columns.some((column) => column.id === 'discount')
    ? SALES_FORM_ANSWER_ROW_HEIGHT
    : SALES_ANSWER_ROW_HEIGHT;

  const rowLayout = useMemo(
    () => computeRowLayout(flattenedRows, answerRowHeight),
    [flattenedRows, answerRowHeight],
  );

  const { startIndex, endIndex, offsetY } = useMemo(() => {
    const start = Math.max(0, findRowIndexAtOffset(rowLayout.offsets, scrollTop) - SALES_OVERSCAN);
    const endOffset = scrollTop + viewportHeight;
    let end = start;
    while (
      end < flattenedRows.length
      && rowLayout.offsets[end] < endOffset + SALES_OVERSCAN * answerRowHeight
    ) {
      end += 1;
    }
    end = Math.min(flattenedRows.length, end + SALES_OVERSCAN);
    return {
      startIndex: start,
      endIndex: end,
      offsetY: rowLayout.offsets[start] ?? 0,
    };
  }, [scrollTop, viewportHeight, flattenedRows.length, rowLayout, answerRowHeight]);

  const visibleRows = useMemo(
    () => flattenedRows.slice(startIndex, endIndex),
    [flattenedRows, startIndex, endIndex],
  );

  const measureBody = useCallback(() => {
    const el = bodyScrollerRef.current;
    if (!el) {
      return;
    }
    setViewportHeight(el.clientHeight);
    setScrollbarWidth(Math.max(0, el.offsetWidth - el.clientWidth));
  }, []);

  const handleScroll = useCallback(() => {
    const el = bodyScrollerRef.current;
    if (!el) {
      return;
    }
    setScrollTop(el.scrollTop);
    measureBody();
    if (headerScrollerRef.current) {
      headerScrollerRef.current.scrollLeft = el.scrollLeft;
    }
  }, [measureBody]);

  useLayoutEffect(() => {
    measureBody();
  }, [measureBody, flattenedRows.length]);

  useLayoutEffect(() => {
    const el = bodyScrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => measureBody());
    observer.observe(el);
    return () => observer.disconnect();
  }, [measureBody]);

  const handleResizeStart = useCallback((
    event: React.MouseEvent,
    column: SaleColumnDef,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      columnId: column.id,
      startX: event.clientX,
      startWidth: colWidth(columnWidths, column),
      minWidth: column.minWidth,
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const state = resizeRef.current;
      if (!state) {
        return;
      }
      const delta = moveEvent.clientX - state.startX;
      onColumnWidthChange(state.columnId, Math.max(state.minWidth, state.startWidth + delta));
    };

    const onMouseUp = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [columnWidths, onColumnWidthChange]);

  const saleColumnIndex = columns.findIndex((column) => column.id === 'sale');
  const saleColumnWidth = saleColumnIndex >= 0
    ? colWidth(columnWidths, columns[saleColumnIndex])
    : 0;
  const discountColumnIndex = columns.findIndex((column) => column.id === 'discount');

  const renderEpisodeSubheader = (row: FlattenedEpisodeRow) => {
    if (saleColumnIndex < 0) {
      return (
        <tr key={row.key} className={styles.episodeSubheader}>
          <td colSpan={columns.length} className={styles.episodeSubheaderTitle}>
            {episodeSubheaderLabel(row)}
          </td>
        </tr>
      );
    }

    const episodeKeys = episodeSelectableKeys.get(row.episodeUuid) ?? [];
    const episodeState = selectionStateForKeys(episodeKeys, selectedKeys);

    return (
      <tr key={row.key} className={styles.episodeSubheader}>
        <td
          colSpan={saleColumnIndex > 0 ? saleColumnIndex : columns.length - 1}
          className={styles.episodeSubheaderTitle}
        >
          {episodeSubheaderLabel(row)}
        </td>
        <td className={styles.episodeSubheaderSale}>
          <div className={styles.episodeSelectAll}>
            <TriStateCheckbox
              id={`sale-ep-${row.episodeUuid}`}
              checked={episodeState.checked}
              indeterminate={episodeState.indeterminate}
              disabled={episodeKeys.length === 0}
              onChange={(checked) => onToggleEpisode?.(row.episodeUuid, checked)}
            />
          </div>
        </td>
      </tr>
    );
  };

  const renderAnswerCell = (
    columnId: SaleTableColumnId,
    answer: SaleAnswerRow,
    rowKey: string,
    selectable: boolean,
  ) => {
    switch (columnId) {
      case 'episode':
        return episodeCellLabel(answer);
      case 'node':
        return answer.nodeTitle;
      case 'stepText':
        return answer.stepText;
      case 'answerText':
        return answer.answerText;
      case 'answerId':
        return answer.answerId;
      case 'answerIndex':
        return answer.answerIndex == null ? '' : answer.answerIndex;
      case 'currencySetting':
        return answer.currencySetting;
      case 'price':
        return answer.price;
      case 'sale':
        return (
          <Form.Check
            type="checkbox"
            checked={selectable && selectedKeys.has(rowKey)}
            disabled={!selectable}
            onChange={(event) => onToggleRow?.(rowKey, event.target.checked)}
            aria-label="Select for sale"
          />
        );
      case 'discount': {
        const selected = answer as SelectedSaleAnswer;
        const discountPercent = selected.discountPercent ?? 0;
        return (
          <Form.Control
            type="number"
            min={0}
            max={100}
            step={1}
            className={
              discountPercent === 0
                ? `${styles.discountInput} ${styles.discountInputZero}`
                : styles.discountInput
            }
            value={discountPercent}
            disabled={discountDisabled}
            onChange={(event) => {
              const parsed = parseInt(event.target.value, 10);
              onDiscountChange?.(rowKey, clampDiscount(parsed));
            }}
          />
        );
      }
      case 'salePrice': {
        const selected = answer as SelectedSaleAnswer;
        return computeSalePrice(answer.price, selected.discountPercent ?? 0);
      }
      default:
        return null;
    }
  };

  const renderAnswerRow = (row: Extract<FlattenedSaleRow, { type: 'answer' }>) => {
    const { answer } = row;
    const selectable = isSelectableSaleAnswer(answer);
    const rowKey = selectable ? saleAnswerKey(answer) : row.key;

    return (
      <tr key={row.key}>
        {columns.map((column) => {
          const content = renderAnswerCell(column.id, answer, rowKey, selectable);
          const isSale = column.id === 'sale';
          return (
            <td
              key={column.id}
              title={typeof content === 'string' ? content : undefined}
              className={isSale ? styles.checkboxCell : undefined}
            >
              {content}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderBodyRow = (row: FlattenedSaleRow) => {
    if (row.type === 'episode') {
      return renderEpisodeSubheader(row);
    }
    return renderAnswerRow(row);
  };

  return (
    <div className={styles.salesTable}>
      <div
        className={styles.headerScroller}
        ref={headerScrollerRef}
        style={{ paddingRight: scrollbarWidth }}
      >
        {saleColumnHeaderSlot && saleColumnIndex >= 0 && (
          <div
            className={styles.selectAllBar}
            style={{ width: totalWidth, minWidth: '100%' }}
          >
            <label className={styles.selectAllLabel} htmlFor="sale-select-all">
              Select All
            </label>
            <div
              className={styles.selectAllCheck}
              style={{ width: `25px`, marginRight: `30px` }}
            >
              {saleColumnHeaderSlot}
            </div>
          </div>
        )}
        {discountColumnHeaderSlot && discountColumnIndex >= 0 && (
          <Table
            bordered
            size="sm"
            className={`${styles.headerTable} ${styles.discountSetTable}`}
            style={{
              width: totalWidth,
              minWidth: '100%',
              ...(saleColumnIndex >= 0
                ? { ['--sale-col-width' as string]: `${saleColumnWidth}px` }
                : {}),
            }}
          >
            <ColGroup columns={columns} columnWidths={columnWidths} />
            <tbody>
              <tr>
                {columns.map((column) => (
                  <td key={column.id}>
                    {column.id === 'discount' ? (
                      <div className={styles.discountSetCell}>
                        {discountColumnHeaderSlot}
                      </div>
                    ) : null}
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
        )}
        <Table
          bordered
          size="sm"
          className={styles.headerTable}
          style={{
            width: totalWidth,
            minWidth: '100%',
            ...(saleColumnIndex >= 0
              ? { ['--sale-col-width' as string]: `${saleColumnWidth}px` }
              : {}),
          }}
        >
          <ColGroup columns={columns} columnWidths={columnWidths} />
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id}>
                  {column.label}
                  <span
                    className={styles.resizeHandle}
                    onMouseDown={(event) => handleResizeStart(event, column)}
                    role="presentation"
                  />
                </th>
              ))}
            </tr>
          </thead>
        </Table>
      </div>
      <div
        className={styles.bodyScroller}
        ref={bodyScrollerRef}
        onScroll={handleScroll}
      >
        <div
          className={styles.spacer}
          style={{ height: rowLayout.totalHeight + SALES_SCROLL_BOTTOM_PAD, minWidth: totalWidth }}
        >
          <div
            className={styles.bodyInner}
            style={{ transform: `translateY(${offsetY}px)`, minWidth: totalWidth }}
          >
            <Table
              bordered
              size="sm"
              className={
                answerRowHeight === SALES_FORM_ANSWER_ROW_HEIGHT
                  ? `${styles.bodyTable} ${styles.bodyTableForm}`
                  : styles.bodyTable
              }
              style={{
                width: totalWidth,
                minWidth: '100%',
                ...(saleColumnIndex >= 0
                  ? { ['--sale-col-width' as string]: `${saleColumnWidth}px` }
                  : {}),
              }}
            >
              <ColGroup columns={columns} columnWidths={columnWidths} />
              <tbody>
                {visibleRows.map(renderBodyRow)}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
